import { useCallback, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addInvoice, deleteInvoice, updateInvoice, toggleInvoiceSelected } from 'features/fastEntrySlice';

/**
 * Custom hook to manage invoices in the FastEntryInvoiceContainer using Redux
 * 
 * @param {Object} options - Configuration options
 * @param {Object} options.ref - Ref object to expose methods to parent component
 * @returns {Object} - Invoice management methods and state
 */
const useInvoiceManagement = ({ ref }) => {
  const dispatch = useDispatch();
  const invoices = useSelector((state) => state.fastEntry.invoices);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getInvoiceData: () => {
      // Format data for the preview_carts action
      const fastEntryItems = [];
      const invoiceIds = [];
      const partialInvoiceIds = [];
      
      invoices.forEach(invoice => {
        if (!invoice.selected) return; // Only include selected invoices
        if (invoice.items && invoice.items.length > 0) {
          // Process each item in the invoice
          invoice.items.forEach(item => {
            // Skip items without style, color, or sizes
            if (!item.style || !item.color || !item.sizes) {
              console.warn("Skipping item missing style, color, or sizes:", item);
              return;
            }
            
            // Make sure we have a dgi_style_sku
            if (!item.dgi_style_sku) {
              console.error("Item missing dgi_style_sku:", item);
              
              // Try to derive it from the style if possible (this is a fallback)
              if (item.style) {
                console.warn("Attempting to use style as dgi_style_sku:", item.style);
                item.dgi_style_sku = item.style;
              } else {
                console.error("Cannot derive dgi_style_sku, skipping item");
                return;
              }
            }
            
            // Process each size with a quantity
            Object.entries(item.sizes).forEach(([size, quantity]) => {
              // Skip sizes with no quantity
              if (!quantity || quantity <= 0) return;
              
              // Create a fast entry item for each size
              const fastEntryItem = {
                dgi_style_sku: item.dgi_style_sku,
                master_color: item.color,
                master_brand: item.brand || "",
                quantity: parseInt(quantity, 10),
                size: size,
                line_item_index: item.id.toString(), // Use the original item ID as a string
                invoice_id: invoice.id.toString(),
                invoice_number: invoice.invoiceNumber || ""
              };
              
              // Validate the fast entry item
              if (!fastEntryItem.dgi_style_sku) {
                console.error("Fast entry item missing dgi_style_sku:", fastEntryItem);
                return;
              }
              
              fastEntryItems.push(fastEntryItem);
            });
          });
        }
      });
      
      return {
        fastEntryItems,
        invoiceIds,
        partialInvoiceIds
      };
    },
    hasSelectedInvoices: () => invoices.some(inv => inv.selected),
    hasSelectedInvoiceWithQuantity: () =>
      invoices.some(inv =>
        inv.selected &&
        inv.items &&
        inv.items.some(item =>
          item.sizes && Object.values(item.sizes).some(qty => qty > 0)
        )
      )
  }));

  // Add a new invoice
  const handleAddNewInvoice = useCallback(() => {
    dispatch(addInvoice());
    // Return the latest invoice for immediate use in the UI
    return {
      id: Date.now(),
      items: [],
      invoiceNumber: ""
    };
  }, [dispatch]);

  // Delete an invoice
  const handleDeleteInvoice = useCallback((invoiceId) => {
    dispatch(deleteInvoice({ id: invoiceId, allowDeleteAll: true }));
  }, [dispatch]);

  // Update an invoice
  const handleUpdateInvoice = useCallback((invoiceId, updatedData) => {
    dispatch(updateInvoice({ invoiceId, updatedData }));
  }, [dispatch]);

  // Toggle invoice selected
  const handleToggleInvoiceSelected = useCallback((invoiceId) => {
    dispatch(toggleInvoiceSelected(invoiceId));
  }, [dispatch]);

  return {
    invoices,
    handleAddNewInvoice,
    handleDeleteInvoice,
    handleUpdateInvoice,
    handleToggleInvoiceSelected
  };
};

export default useInvoiceManagement;